import axios from 'axios';
import { Texto, TextoDetaloj, APIResponse, User } from '../types';
import { PAGINATION_CONFIG } from '../config/constants';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'https://ikurso.esperanto-france.org/api.php' // http://localhost:8080/api.php
  : 'https://ikurso.esperanto-france.org/api.php';

// Fonction pour récupérer le token JWT
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// Intercepteur pour ajouter le token JWT aux requêtes
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Ajouter l'intercepteur pour inclure le token JWT dans les headers
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide, supprimer le token
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    }
    return Promise.reject(error);
  }
);

export const tekstojService = {
  // Récupérer la liste des textes
  getTekstoj: async (): Promise<Texto[]> => {
    try {
      const response = await api.get('?path=tekstoj');

      // L'API retourne un objet avec une propriété 'data' contenant le tableau
      if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray(response.data.data)) {
        // Transformer les données pour correspondre à notre interface Texto
        const tekstoj = response.data.data.map((item: any) => {
          return {
            id: item.id,
            titolo: item.titolo,
            aŭtoro: item.auxtoro || item.aŭtoro, // Note: l'API utilise 'auxtoro' au lieu de 'aŭtoro'
            nivelo: item.nivelo,
            longeco: parseInt(item.vortoj) || 0, // 'vortoj' contient le nombre de mots
            priskribo: item.fonto, // Utiliser la source comme description
            ŝlosilvortoj: item.etikedoj ? item.etikedoj.split(',').map((tag: string) => tag.trim()) : [],
            audioUrl: item.sono || null,
            sono: item.sono || null,
            leganto: item.leganto || null, // Inclure le nom de la personne qui lit
            arthur_id: item.arthur_id || null // Identifiant du livre dans la base Arthur
          };
        });

        return tekstoj;
      }

      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération des textes:', error);
      throw new Error('Impossible de récupérer les textes');
    }
  },

  // Récupérer les détails d'un texte spécifique
  getTekstoDetaloj: async (id: string): Promise<TextoDetaloj> => {
    try {
      const response = await api.get(`?path=tekstoj/${id}`);

      // S'assurer que les données sont correctement formatées
      const data = response.data;
      if (data && typeof data === 'object') {
        // Transformer les données pour correspondre à l'interface TextoDetaloj
        const tekstoDetaloj: TextoDetaloj = {
          id: data.id || id,
          titolo: data.titolo || 'Titre inconnu',
          aŭtoro: data.aŭtoro || data.auxtoro || 'Auteur inconnu',
          nivelo: data.nivelo || 'Inconnu',
          longeco: data.longeco || parseInt(data.vortoj) || 0,
          priskribo: data.priskribo || data.fonto || '',
          ŝlosilvortoj: data.ŝlosilvortoj || (data.etikedoj ? (data.etikedoj as string).split(',').map((tag: string) => tag.trim()) : []),
          audioUrl: data.sono || data.audioUrl || null, // Utiliser le champ "sono" de l'API
          sono: data.sono || null, // Garder aussi le champ original
          enhavo: Array.isArray(data.enhavo) ? data.enhavo.map((section: any) => section.teksto) : (data.enhavo || data.contenu || ''),
          traduko: data.traduko || undefined,
          notoj: data.notoj || undefined,
          vortaro: Array.isArray(data.enhavo) ? data.enhavo.reduce((acc: any, section: any) => ({ ...acc, ...section.vortaro }), {}) : (data.vortaro || undefined),
          leganto: data.leganto || null,
          arthur_id: data.arthur_id || null
        };

        return tekstoDetaloj;
      }

      throw new Error('Format de données invalide');
    } catch (error) {
      console.error(`Erreur lors de la récupération du texte ${id}:`, error);
      throw new Error(`Impossible de récupérer le texte ${id}`);
    }
  },

  // Rechercher des textes avec filtres
  searchTekstoj: async (filtroj: any, offset: number = 0): Promise<APIResponse> => {
    try {
      // Transformer les filtres pour correspondre aux noms attendus par l'API
      const apiParams: any = {
        offset: offset,
        limit: PAGINATION_CONFIG.ITEMS_PER_PAGE
      };
      
      if (filtroj.serĉo && filtroj.serĉo.trim() !== '') {
        apiParams.q = filtroj.serĉo; // paramètre de recherche
      }
      
      if (filtroj.nivelo && filtroj.nivelo.trim() !== '') {
        // Transformer les valeurs de niveau en plages numériques
        switch (filtroj.nivelo) {
          case 'facile':
            apiParams.nivelo_min = 0;
            apiParams.nivelo_max = 999;
            break;
          case 'intermediaire':
            apiParams.nivelo_min = 1000;
            apiParams.nivelo_max = 1999;
            break;
          case 'avance':
            apiParams.nivelo_min = 2000;
            apiParams.nivelo_max = 9999; // ou une valeur très élevée
            break;
          default:
            // Si c'est une valeur numérique directe, l'utiliser telle quelle
            if (!isNaN(parseInt(filtroj.nivelo))) {
              apiParams.nivelo = filtroj.nivelo;
            }
        }
      }
      
      
      if (filtroj.longecoMin > 200 || filtroj.longecoMax < 4000) {
        apiParams.vortoj_min = filtroj.longecoMin;
        apiParams.vortoj_max = filtroj.longecoMax;
      }
      
      if (filtroj.ŝlosilvortoj && filtroj.ŝlosilvortoj.length > 0) {
        apiParams.etikedoj = filtroj.ŝlosilvortoj.join(',');
      }
      
      if (filtroj.hasSono) {
        apiParams.has_sono = 'true';
      }

      // Gestion de l'ordre de tri
      if (filtroj.order && filtroj.sort) {
        apiParams.sort = filtroj.order;  // Le champ à trier
        apiParams.order = filtroj.sort;  // ASC ou DESC
      }

      // Toujours utiliser les paramètres avec offset pour la pagination
      const response = await api.get('?path=tekstoj', { params: apiParams });

      // L'API retourne un objet avec une propriété 'data' contenant le tableau
      if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray(response.data.data)) {
        // Transformer les données APITeksto vers Texto
        const tekstoj = response.data.data.map((item: any) => {
          return {
            id: item.id,
            titolo: item.titolo,
            aŭtoro: item.auxtoro || item.aŭtoro, // Note: l'API utilise 'auxtoro' au lieu de 'aŭtoro'
            nivelo: item.nivelo,
            longeco: parseInt(item.vortoj) || 0, // 'vortoj' contient le nombre de mots
            priskribo: item.fonto, // Utiliser la source comme description
            ŝlosilvortoj: item.etikedoj ? item.etikedoj.split(',').map((tag: string) => tag.trim()) : [],
            audioUrl: item.sono || null,
            sono: item.sono || null,
            leganto: item.leganto || null, // Inclure le nom de la personne qui lit
            arthur_id: item.arthur_id || null // Identifiant du livre dans la base Arthur
          };
        });
        
        // Retourner la structure APIResponse avec pagination
        return {
          data: tekstoj,
          pagination: response.data.pagination || {
            total: tekstoj.length,
            limit: PAGINATION_CONFIG.ITEMS_PER_PAGE,
            offset: offset,
            count: tekstoj.length
          }
        };
      }

      return {
        data: [],
        pagination: {
          total: 0,
          limit: PAGINATION_CONFIG.ITEMS_PER_PAGE,
          offset: offset,
          count: 0
        }
      };
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      throw new Error('Impossible de rechercher les textes');
    }
  }
};

export const legotajxojService = {
  // Sauvegarder un texte pour plus tard
  saveTeksto: async (tekstoId: string): Promise<void> => {
    try {
      const response = await api.post('?path=legotajxoj', {
        teksto_id: tekstoId
      });
      
      console.log('Save teksto response:', response.data);
    } catch (error: any) {
      console.error('Save teksto error:', error);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Erreur lors de la sauvegarde du texte');
      }
    }
  },

  // Récupérer les textes sauvegardés de l'utilisateur
  getSavedTekstoj: async (): Promise<Texto[]> => {
    try {
      const response = await api.get('?path=legotajxoj');
      console.log('Get saved tekstoj response:', response.data);
      
      if (response.data && Array.isArray(response.data.legotajxoj)) {
        // Transformer les données de legotajxoj vers Texto
        const tekstoj = response.data.legotajxoj.map((item: any) => ({
          id: item.teksto_id, // Utiliser teksto_id comme identifiant principal
          titolo: item.titolo,
          aŭtoro: item.auxtoro,
          nivelo: item.nivelo.toString(),
          longeco: parseInt(item.vortoj) || 0,
          priskribo: item.fonto,
          ŝlosilvortoj: item.etikedoj ? item.etikedoj.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag.length > 0) : [],
          audioUrl: item.sono || null,
          sono: item.sono || null,
          leganto: item.leganto || null
        }));
        
        return tekstoj;
      }
      
      return [];
    } catch (error: any) {
      console.error('Get saved tekstoj error:', error);
      throw new Error('Erreur lors du chargement de la liste à lire');
    }
  },

  // Supprimer un texte de la liste sauvegardée
  removeTeksto: async (tekstoId: string): Promise<void> => {
    try {
      const response = await api.delete(`?path=legotajxoj/${tekstoId}`);
      console.log('Remove teksto response:', response.data);
    } catch (error: any) {
      console.error('Remove teksto error:', error);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Erreur lors de la suppression du texte');
      }
    }
  }
};

export const legitajxojService = {
  // Enregistrer le début de lecture d'un texte
  recordTextStart: async (tekstoId: string): Promise<void> => {
    try {
      const response = await api.post('?path=legitajxoj', {
        teksto_id: tekstoId,
        komenc_timestamp: new Date().toISOString()
      });
      
      console.log('Record text start response:', response.data);
    } catch (error: any) {
      console.error('Record text start error:', error);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Erreur lors de l\'enregistrement du début de lecture');
      }
    }
  },

  // Marquer un texte comme terminé avec note et commentaire
  finishText: async (tekstoId: string, rating: number, comment: string): Promise<void> => {
    try {
      const response = await api.patch(`?path=legitajxoj/${tekstoId}`, {
        fin_timestamp: new Date().toISOString(),
        noto: rating,
        komentaro: comment.trim()
      });
      
      console.log('Finish text response:', response.data);
    } catch (error: any) {
      console.error('Finish text error:', error);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Erreur lors de la finalisation du texte');
      }
    }
  },

  // Récupérer l'historique des textes lus par l'utilisateur
  getReadTexts: async (): Promise<Texto[]> => {
    try {
      const response = await api.get('?path=legitajxoj');
      console.log('Read texts response:', response.data);
      
      if (response.data && Array.isArray(response.data.legitajxoj)) {
        // Transformer les données de legitajxoj vers Texto
        const tekstoj = response.data.legitajxoj.map((item: any) => ({
          id: item.teksto_id,
          titolo: item.titolo,
          aŭtoro: item.auxtoro,
          nivelo: item.nivelo.toString(),
          longeco: parseInt(item.vortoj) || 0,
          priskribo: item.fonto,
          ŝlosilvortoj: item.etikedoj ? item.etikedoj.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag.length > 0) : [],
          audioUrl: item.sono || null,
          sono: item.sono || null,
          leganto: item.leganto || null
        }));
        
        return tekstoj;
      }
      
      return [];
    } catch (error: any) {
      console.error('Get read texts error:', error);
      throw new Error('Erreur lors du chargement de l\'historique des lectures');
    }
  }
};

export const profilService = {
  // Récupérer le profil détaillé d'un utilisateur
  getProfil: async (userId: string): Promise<any> => {
    try {
      const response = await api.get(`?path=personoj/${userId}`);
      console.log('Profile response:', response.data);
      
      if (response.data && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Données de profil non trouvées');
    } catch (error: any) {
      console.error('Profile fetch error:', error);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.status === 404) {
        throw new Error('Profil non trouvé');
      } else if (error.response?.status === 403) {
        throw new Error('Accès non autorisé au profil');
      } else {
        throw new Error('Erreur lors du chargement du profil');
      }
    }
  }
};

export const authService = {
  // Vérifier l'authentification de l'utilisateur actuel
  getMe: async (): Promise<User | null> => {
    try {
      const response = await api.get('?path=auth/me');
      console.log('Auth me response:', response.data);
      
      if (response.data && response.data.user) {
        const userData = response.data.user;
        return {
          id: userData.id,
          nomo: userData.enirnomo || userData.nomo, // Support pour les deux formats
          personnomo: userData.personnomo || userData.familinomo,
          retpoŝto: userData.retadreso || userData.retpoŝto, // Support pour les deux formats
          rolo: userData.rajtoj || userData.rolo // Support pour les deux formats
        };
      }
      
      return null;
    } catch (error) {
      console.log('User not authenticated:', error);
      return null;
    }
  },

  // Connecter un utilisateur
  login: async (identigilo: string, pasvorto: string): Promise<{ user: User; token: string }> => {
    try {
      const response = await api.post('?path=auth/login', {
        identigilo,
        pasvorto
      });
      
      console.log('Login response:', response.data);
      
      if (response.data && response.data.user && response.data.access_token) {
        const userData = response.data.user;
        const token = response.data.access_token;
        
        // Sauvegarder le token JWT
        localStorage.setItem('auth_token', token);
        
        // Mapper les champs de l'API vers l'interface User
        const user: User = {
          id: userData.id,
          nomo: userData.enirnomo, // enirnomo = nom d'utilisateur
          personnomo: userData.personnomo || userData.familinomo, // utilisar le nom de famille si pas de prénom
          retpoŝto: userData.retadreso, // retadreso = adresse email
          rolo: userData.rajtoj // rajtoj = droits/rôle
        };
        
        console.log('Mapped user:', user);
        return { user, token };
      }
      
      throw new Error('Réponse de connexion invalide - aucune donnée utilisateur trouvée');
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.status === 401) {
        throw new Error('Identifiant ou mot de passe incorrect');
      } else {
        throw new Error('Erreur lors de la connexion');
      }
    }
  },

  // Déconnecter un utilisateur
  logout: async (): Promise<void> => {
    try {
      await api.post('?path=auth/logout');
    } catch (error) {
      // Même si la requête échoue, on supprime les données locales
      console.warn('Logout request failed, but clearing local data anyway');
    } finally {
      // Supprimer le token et les données utilisateur
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    }
  }
};

export default api; 